/**
 * Tasks Page — Task Management
 *
 * Features:
 *   - Task list with filters (My Tasks, All Tasks, By Status, By Priority)
 *   - Create task dialog
 *   - Task status workflow: open -> in_progress -> completed
 *   - Priority badges (urgent=red, high=amber, medium=yellow, low=gray)
 *   - Due date with overdue highlighting
 *
 * Data from:
 *   GET   /api/v1/impact-core/tasks/        — task list
 *   POST  /api/v1/impact-core/tasks/        — create task
 *   PATCH /api/v1/impact-core/tasks/:id/status — transition status
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckSquare,
  Plus,
  X,
  Search,
  ChevronRight,
  Clock,
  User,
} from 'lucide-react';
import { format, parseISO, isPast, isToday } from 'date-fns';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useTranslation } from '@/i18n';
import { formatStatus } from '@/utils/formatStatus';
import type { Fund } from '@/types';

// ─── Types ──────────────────────────────────────────────────────

interface Task {
  id: number;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigned_to_id?: number;
  assigned_to_name?: string;
  fund_id?: number;
  fund_name?: string;
  due_date?: string;
  created_by_name?: string;
  created_at: string;
  updated_at?: string;
}

type TaskStatus = 'open' | 'in_progress' | 'completed';
type TaskPriority = 'urgent' | 'high' | 'medium' | 'low';

interface TaskCreatePayload {
  title: string;
  description?: string;
  priority: string;
  assigned_to_id?: number;
  fund_id?: number;
  due_date?: string;
}

interface OrgUser {
  id: number;
  full_name: string;
  email: string;
}

type FilterTab = 'my_tasks' | 'all' | 'open' | 'in_progress' | 'completed';

const PRIORITY_STYLES: Record<string, { variant: 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
  urgent: { variant: 'danger', label: 'Urgent' },
  high: { variant: 'warning', label: 'High' },
  medium: { variant: 'info', label: 'Medium' },
  low: { variant: 'default', label: 'Low' },
};

const STATUS_VARIANTS: Record<string, 'default' | 'info' | 'success'> = {
  open: 'default',
  in_progress: 'info',
  completed: 'success',
};

const STATUS_TRANSITIONS: Record<string, TaskStatus[]> = {
  open: ['in_progress'],
  in_progress: ['completed', 'open'],
  completed: [],
};

// ─── Main Component ─────────────────────────────────────────────

export function TasksPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filterTab, setFilterTab] = useState<FilterTab>('my_tasks');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<{ id: number; from: string } | null>(null);

  const tasksQuery = useQuery<Task[]>({
    queryKey: ['ic', 'tasks'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/tasks/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: TaskStatus }) => {
      const res = await apiClient.patch(`/api/v1/impact-core/tasks/${id}/status`, { status });
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'tasks'] });
      setTransitionTarget(null);
    },
  });

  const tasks = tasksQuery.data ?? [];

  const filtered = useMemo(() => {
    let result = tasks;

    // Tab filter
    if (filterTab === 'my_tasks') {
      result = result.filter((t) => t.assigned_to_id === user?.id);
    } else if (filterTab !== 'all') {
      result = result.filter((t) => t.status === filterTab);
    }

    // Search filter
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q) ||
          (t.assigned_to_name ?? '').toLowerCase().includes(q) ||
          (t.fund_name ?? '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [tasks, filterTab, search, user?.id]);

  // Summary counts
  const myTaskCount = tasks.filter((t) => t.assigned_to_id === user?.id).length;
  const openCount = tasks.filter((t) => t.status === 'open').length;
  const inProgressCount = tasks.filter((t) => t.status === 'in_progress').length;
  const overdueCount = tasks.filter(
    (t) => t.due_date && t.status !== 'completed' && isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date))
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">Tasks</h1>
          <p className="text-xs text-ic-text-tertiary">
            {tasks.length} total | {myTaskCount} assigned to you
            {overdueCount > 0 && (
              <span className="text-red-400"> | {overdueCount} overdue</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          New Task
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <SummaryCard label="My Tasks" value={myTaskCount} icon={<User className="h-4 w-4" />} />
        <SummaryCard label="Open" value={openCount} icon={<CheckSquare className="h-4 w-4" />} />
        <SummaryCard label="In Progress" value={inProgressCount} icon={<Clock className="h-4 w-4" />} />
        <SummaryCard
          label="Overdue"
          value={overdueCount}
          icon={<Clock className="h-4 w-4" />}
          highlight={overdueCount > 0}
        />
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-4">
        <div className="border-b border-ic-border flex gap-0">
          <FilterBtn label="My Tasks" active={filterTab === 'my_tasks'} onClick={() => setFilterTab('my_tasks')} count={myTaskCount} />
          <FilterBtn label="All" active={filterTab === 'all'} onClick={() => setFilterTab('all')} count={tasks.length} />
          <FilterBtn label="Open" active={filterTab === 'open'} onClick={() => setFilterTab('open')} count={openCount} />
          <FilterBtn label="In Progress" active={filterTab === 'in_progress'} onClick={() => setFilterTab('in_progress')} count={inProgressCount} />
          <FilterBtn label="Completed" active={filterTab === 'completed'} onClick={() => setFilterTab('completed')} count={tasks.filter((t) => t.status === 'completed').length} />
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary" />
        <input
          type="text"
          placeholder="Search tasks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-ic-border bg-ic-input py-2 pl-9 pr-3 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
        />
      </div>

      {/* Tasks Table */}
      <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
        {tasksQuery.isLoading ? (
          <TableSkeleton />
        ) : tasksQuery.isError ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-xs text-red-400">Failed to load tasks.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-xs text-ic-text-tertiary">
              {tasks.length === 0 ? 'No tasks yet. Create one to get started.' : 'No tasks match your filters.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-xs ic-table">
            <thead>
              <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                <th className="px-4 pb-2 pt-3 font-medium">Task</th>
                <th className="px-4 pb-2 pt-3 font-medium">Priority</th>
                <th className="px-4 pb-2 pt-3 font-medium">Status</th>
                <th className="px-4 pb-2 pt-3 font-medium">Assigned To</th>
                <th className="px-4 pb-2 pt-3 font-medium">Fund</th>
                <th className="px-4 pb-2 pt-3 font-medium">Due Date</th>
                <th className="px-4 pb-2 pt-3 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((task) => {
                const transitions = STATUS_TRANSITIONS[task.status] ?? [];
                const isOverdue =
                  task.due_date &&
                  task.status !== 'completed' &&
                  isPast(parseISO(task.due_date)) &&
                  !isToday(parseISO(task.due_date));
                const priority = PRIORITY_STYLES[task.priority];

                return (
                  <tr key={task.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50 transition-colors">
                    <td className="py-2.5 px-4">
                      <div className="flex flex-col">
                        <span className={`text-xs font-medium ${
                          task.status === 'completed' ? 'text-ic-text-tertiary line-through' : 'text-ic-text-primary'
                        }`}>
                          {task.title}
                        </span>
                        {task.description && (
                          <span className="text-[10px] text-ic-text-tertiary line-clamp-1 mt-0.5">
                            {task.description}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge
                        label={priority?.label ?? task.priority}
                        variant={priority?.variant ?? 'default'}
                        size="sm"
                      />
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge
                        label={formatStatus(task.status, t)}
                        variant={STATUS_VARIANTS[task.status] ?? 'default'}
                        size="sm"
                      />
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">
                      {task.assigned_to_name ?? '--'}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">
                      {task.fund_name ?? '--'}
                    </td>
                    <td className="py-2.5 px-4">
                      {task.due_date ? (
                        <span className={`text-xs ${isOverdue ? 'font-medium text-red-400' : 'text-ic-text-tertiary'}`}>
                          {format(parseISO(task.due_date), 'dd MMM yyyy')}
                          {isOverdue && <span className="ml-1 text-[10px]">(overdue)</span>}
                        </span>
                      ) : (
                        <span className="text-xs text-ic-text-tertiary">--</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">
                      {transitions.length > 0 && (
                        <div className="relative">
                          <button
                            onClick={() => setTransitionTarget(
                              transitionTarget?.id === task.id ? null : { id: task.id, from: task.status }
                            )}
                            className="text-ic-text-tertiary hover:text-ic-accent transition-colors"
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                          {transitionTarget?.id === task.id && (
                            <div className="absolute right-0 top-full z-20 mt-1 min-w-[120px] rounded-md border border-ic-border bg-ic-sidebar py-1 shadow-lg">
                              {transitions.map((nextStatus) => (
                                <button
                                  key={nextStatus}
                                  onClick={() => statusMutation.mutate({ id: task.id, status: nextStatus })}
                                  disabled={statusMutation.isPending}
                                  className="block w-full px-3 py-1.5 text-left text-xs text-ic-text-secondary hover:bg-ic-hover hover:text-ic-text-primary disabled:opacity-50"
                                >
                                  {formatStatus(nextStatus, t)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Dialog */}
      {showCreate && (
        <TaskCreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'tasks'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Create Task Dialog ─────────────────────────────────────────

function TaskCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assignedToId, setAssignedToId] = useState('');
  const [fundId, setFundId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const usersQuery = useQuery<OrgUser[]>({
    queryKey: ['ic', 'org-users'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/users/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const mutation = useMutation({
    mutationFn: async (payload: TaskCreatePayload) => {
      const res = await apiClient.post('/api/v1/impact-core/tasks/', payload);
      return res.data;
    },
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setErrors({ _general: axiosErr.response?.data?.detail ?? 'Failed to create task.' });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!title.trim()) { setErrors({ title: 'Title is required' }); return; }

    const payload: TaskCreatePayload = {
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      assigned_to_id: assignedToId ? Number(assignedToId) : undefined,
      fund_id: fundId ? Number(fundId) : undefined,
      due_date: dueDate || undefined,
    };

    mutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">Create Task</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          <FormField label="Title *" error={errors.title}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Review Q2 ESG submissions"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </FormField>

          <FormField label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Task details..."
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none resize-none"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Assign To">
              <select
                value={assignedToId}
                onChange={(e) => setAssignedToId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">Unassigned</option>
                {(usersQuery.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </FormField>
            <FormField label="Fund">
              <select
                value={fundId}
                onChange={(e) => setFundId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">No fund</option>
                {(fundsQuery.data ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Priority">
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </FormField>
            <FormField label="Due Date">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              />
            </FormField>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Creating...' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  icon,
  highlight,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-lg border p-3 ${
      highlight ? 'border-red-500/30 bg-red-500/5' : 'border-ic-border bg-ic-card'
    }`}>
      <div className="flex items-center gap-2 text-ic-text-tertiary">
        {icon}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className={`mt-1 text-xl font-semibold tracking-tight ${
        highlight ? 'text-red-400' : 'text-ic-text-primary'
      }`}>
        {value}
      </p>
    </div>
  );
}

function FilterBtn({
  label,
  active,
  onClick,
  count,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
        active
          ? 'border-ic-accent text-ic-accent'
          : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
      }`}
    >
      {label}
      <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
        active ? 'bg-ic-accent/15 text-ic-accent' : 'bg-slate-700/50 text-slate-400'
      }`}>
        {count}
      </span>
    </button>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{label}</label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
    </div>
  );
}
